// Web push subscription helper. Registers the service worker, asks permission, subscribes via
// the browser PushManager using the server's VAPID public key, and stores the subscription on
// the backend so the day-3 monitoring follow-up (and future alerts) can reach the driver even
// when the tab is closed. All calls are best-effort and safe to invoke from a click handler.

import { getApiBaseUrl } from './chatIdentity';

// Per-device opt-out preference. Push is ON by default — we only treat it as off when the user
// explicitly turns it off in Settings. Stored per browser (push subscriptions are per-device).
const PUSH_PREF_KEY = 'carfido_push_pref';

export function getPushPreference() {
    try {
        return window.localStorage.getItem(PUSH_PREF_KEY) === 'off' ? 'off' : 'on';
    } catch (e) {
        return 'on';
    }
}

export function setPushPreference(value) {
    try {
        window.localStorage.setItem(PUSH_PREF_KEY, value === 'off' ? 'off' : 'on');
    } catch (e) { /* ignore */ }
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
    return output;
}

function pushSupported() {
    return typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window;
}

// Enable web push. Returns { ok, reason }. Must be called from a user gesture so the browser
// permission prompt is allowed. Safe to call repeatedly (idempotent).
export async function enableWebPush() {
    if (!pushSupported()) return { ok: false, reason: 'unsupported' };

    const apiBase = getApiBaseUrl();
    let config;
    try {
        const resp = await fetch(`${apiBase}/api/push/vapid-public-key`, { credentials: 'include' });
        config = await resp.json();
    } catch (e) {
        return { ok: false, reason: 'config_unavailable' };
    }
    if (!config?.enabled || !config?.public_key) return { ok: false, reason: 'not_configured' };

    let registration;
    try {
        registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
    } catch (e) {
        return { ok: false, reason: 'sw_failed' };
    }

    // Notification.requestPermission resolves 'granted' | 'denied' | 'default'.
    let permission = Notification.permission;
    if (permission === 'default') {
        try { permission = await Notification.requestPermission(); } catch (e) { permission = 'denied'; }
    }
    if (permission !== 'granted') return { ok: false, reason: permission === 'denied' ? 'denied' : 'dismissed' };

    let subscription;
    try {
        subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(config.public_key),
            });
        }
    } catch (e) {
        return { ok: false, reason: 'subscribe_failed' };
    }

    try {
        const json = subscription.toJSON();
        const resp = await fetch(`${apiBase}/api/push/subscribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                endpoint: json.endpoint,
                keys: json.keys,
                user_agent: navigator.userAgent,
            }),
        });
        // fetch only throws on network errors — a 401/500 resolves normally, so check it
        // explicitly, otherwise we'd report success while nothing was stored.
        if (!resp.ok) {
            return { ok: false, reason: resp.status === 401 ? 'unauthenticated' : 'save_failed', status: resp.status };
        }
    } catch (e) {
        return { ok: false, reason: 'save_failed' };
    }
    return { ok: true };
}

// Current push state for the settings toggle: is push supported by the browser, configured on
// the server (VAPID set), the browser permission, and whether this device is subscribed.
export async function getWebPushState() {
    const state = { supported: pushSupported(), configured: false, permission: 'default', subscribed: false };
    if (!state.supported) return state;
    state.permission = Notification.permission;
    try {
        const resp = await fetch(`${getApiBaseUrl()}/api/push/vapid-public-key`, { credentials: 'include' });
        const cfg = await resp.json();
        state.configured = Boolean(cfg?.enabled && cfg?.public_key);
    } catch (e) { /* leave configured=false */ }
    try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
            const sub = await reg.pushManager.getSubscription();
            state.subscribed = Boolean(sub);
        }
    } catch (e) { /* leave subscribed=false */ }
    return state;
}

// Turn push off: unsubscribe this device and tell the backend to drop the subscription.
export async function disableWebPush() {
    if (!pushSupported()) return { ok: false, reason: 'unsupported' };
    try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                const endpoint = sub.endpoint;
                try { await sub.unsubscribe(); } catch (e) { /* ignore */ }
                try {
                    await fetch(`${getApiBaseUrl()}/api/push/unsubscribe`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ endpoint }),
                    });
                } catch (e) { /* ignore */ }
            }
        }
        return { ok: true };
    } catch (e) {
        return { ok: false, reason: 'error' };
    }
}

// Best-effort: enable push only if the user hasn't already denied it, and never throw. Use this
// from flows where we'd like a reminder (e.g. answering "not sure") without nagging.
export async function tryEnableWebPushQuietly() {
    try {
        if (!pushSupported()) return { ok: false, reason: 'unsupported' };
        if (getPushPreference() === 'off') return { ok: false, reason: 'opted_out' };
        if (Notification.permission === 'denied') return { ok: false, reason: 'denied' };
        return await enableWebPush();
    } catch (e) {
        return { ok: false, reason: 'error' };
    }
}
